import React, { useContext, useEffect, useRef, useState } from 'react'
import { TenantSettingsContext, UserContext } from '../../App'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useNavigate, useParams } from 'react-router-dom'
import defaultEmployeeTeams from '../../helpers/auth/_defaultEmployeeTeams'
import TipTap from '../../components/TipTap/TipTap'
import API from '../../services/API'
import {
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material'
import { Delete, NoteAdd } from '@mui/icons-material'

const NewCrewVault = ({ editMode }) => {
  const { user } = useContext(UserContext)
  const { orgSettings } = useContext(TenantSettingsContext)?.orgConfig
  const [loading, setLoading] = useState(false)
  const [roleOptions, setRoleOptions] = useState([])
  const [teamOptions, setTeamOptions] = useState([])
  const navigate = useNavigate()
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [roles, setRoles] = useState(roleOptions)
  const [teams, setTeams] = useState(teamOptions)
  const [tags, setTags] = useState([])
  const [attachments, setAttachments] = useState([])
  const [newAttachments, setNewAttachments] = useState([])
  const fileInput = useRef()

  const { existingID } = useParams()

  useEffect(() => {
    if (editMode) {
      setLoading(true)
      API.get('/crewvault/' + existingID)
        .then((res) => {
          if (res.status === 200) {
            setTitle(res.data?.title || '')
            setContent(res.data?.content || '')
            setRoles(res.data?.roles || [])
            setTeams(res.data?.teams || [])
            setTags(res.data?.tags || [])
            setAttachments(res.data?.attachments || [])
          }
        })
        .finally(() => setLoading(false))
    }
  }, [existingID])

  useEffect(() => {
    if (orgSettings?.roles?.length > 0) {
      setRoleOptions([...orgSettings.roles])
      setRoles([...orgSettings.roles])
    }
    if (orgSettings?.teams?.length > 0) {
      setTeamOptions([...orgSettings.additionalTeams, ...orgSettings.teams])
      setTeams([...orgSettings.additionalTeams, ...orgSettings.teams])
    }
  }, [orgSettings])

  const handleDeleteAttachment = (index) => {
    setNewAttachments((prev) => prev.map((x, i) => i !== index))
  }

  const handleDeleteExisting = (attachment) => {
    if (attachment.s3key) {
      API.post('/deletes3object', {
        key: attachment.s3key,
      })
    }
    if (attachment._id) {
      API.put('/crewvault', {
        _id: existingID,
        $pull: {
          attachments: {
            _id: attachment._id,
          },
        },
      })
    }
    setAttachments((prev) => prev.filter((x) => x._id !== attachment._id))
  }

  const handleSubmit = async () => {
    let s3keys = []
    const uploadAttachments = newAttachments.map(async (attachment) => {
      const res = await API.post('/gets3uploadurl', {
        folder: 'crewvault',
        extension: attachment.type.split('/')[1],
      })
      await API.put(res.data.url, attachment, {
        headers: {
          'Content-Type':
            attachment.type.split('/')[1] === 'pdf'
              ? 'application/pdf'
              : 'multipart/form-data',
        },
      })
      return {
        fileName: attachment.name,
        s3key: res.data.key,
      }
    })
    const keys = await Promise.all(uploadAttachments)
    s3keys = keys
    if (!editMode) {
      API.post('/crewvault', {
        title,
        content,
        tags,
        roles,
        teams,
        attachments: s3keys,
      }).then((res) => {
        if (res.status === 200) {
          navigate(`/crewvault/${res.data}`)
        }
      })
    } else {
      API.put('/crewvault', {
        _id: existingID,
        title,
        content,
        tags,
        roles,
        teams,
        $push: {
          attachments: s3keys,
        },
      }).then((res) => {
        if (res.status === 200) {
          navigate(`/crewvault/${res.data}`)
        }
      })
    }
  }

  return (
    <Stack
      gap={2}
      padding={3}
      component={Paper}
      elevation={2}
      sx={{ borderRadius: 2 }}
    >
      <Typography variant="title2">
        {editMode ? 'Edit' : 'Create'} Crew Vault Item
      </Typography>
      {loading && <LinearProgress />}
      <TextField
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        label="Title"
        fullWidth
      />
      <TipTap value={content} onChange={(newValue) => setContent(newValue)} />
      <Stack border="solid 1px grey" borderRadius={1} padding={1}>
        <Stack
          direction="row"
          gap={1}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="title3" fontSize="1rem">
            Attachments
          </Typography>
          <Button
            color="success"
            variant="outlined"
            startIcon={<NoteAdd color="success" />}
            size="small"
            onClick={() => fileInput.current.click()}
          >
            Add New
            <input
              ref={fileInput}
              type="file"
              style={{ display: 'none' }}
              accept={['application/pdf', '.doc', '.docx', 'image/*']}
              onChange={(e) => {
                setNewAttachments((prev) => [...prev, e.target.files[0]])
              }}
            />
          </Button>
        </Stack>
        <Table padding="none" sx={{ marginTop: 2 }}>
          <TableBody>
            {attachments.map((attachment, index) => {
              if (attachment) {
                return (
                  <TableRow key={index}>
                    <TableCell>{attachment.fileName}</TableCell>
                    <TableCell align="right">
                      <Delete
                        color="error"
                        onClick={() => handleDeleteExisting(attachment)}
                        sx={{ cursor: 'pointer' }}
                      />
                    </TableCell>
                  </TableRow>
                )
              }
            })}
            {newAttachments.map((attachment, index) => {
              if (attachment) {
                return (
                  <TableRow key={index}>
                    <TableCell>{attachment.name}</TableCell>
                    <TableCell align="right">
                      <Delete
                        color="error"
                        onClick={() => handleDeleteAttachment(index)}
                        sx={{ cursor: 'pointer' }}
                      />
                    </TableCell>
                  </TableRow>
                )
              }
            })}
          </TableBody>
        </Table>
      </Stack>

      <Autocomplete
        title="Tags"
        value={tags}
        onChange={(e, value) => setTags(value)}
        freeSolo
        renderInput={(params) => (
          <TextField
            {...params}
            label="Tags"
            helperText="Enter your own tag and then press 'Enter'"
          />
        )}
        multiple
        options={[]}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index })
            return (
              <Chip
                variant="outlined"
                size="small"
                label={option}
                key={key}
                sx={{ borderRadius: 1 }}
                color="anPurple"
                {...tagProps}
              />
            )
          })
        }
      />
      <Autocomplete
        title="Roles"
        value={roles}
        onChange={(e, value) => setRoles(value)}
        options={roleOptions}
        renderInput={(params) => <TextField {...params} label="Roles" />}
        multiple
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index })
            return (
              <Chip
                variant="outlined"
                size="small"
                label={option}
                key={key}
                sx={{ borderRadius: 1 }}
                color="primary"
                {...tagProps}
              />
            )
          })
        }
      />
      <Autocomplete
        title="Team"
        value={teams}
        onChange={(e, value) => setTeams(value)}
        options={teamOptions}
        renderInput={(params) => (
          <TextField {...params} label="Teams" variant="outlined" />
        )}
        multiple
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index })
            return (
              <Chip
                variant="outlined"
                size="small"
                label={option}
                key={key}
                sx={{ borderRadius: 1 }}
                color="contrast"
                {...tagProps}
              />
            )
          })
        }
      />
      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="anBlue"
          size="large"
          sx={{ borderRadius: 2 }}
        >
          Save Item
        </Button>
      </Box>
    </Stack>
  )
}

export default NewCrewVault
